// frontend/src/pages/public/FindFamilyDoctor/FindFamilyDoctorPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import PublicFooter from "../../../components/PublicFooter/PublicFooter";
import {
  FIND_FAMILY_DOCTOR_CITY_PAGE_LIST,
  getFindFamilyDoctorCityPage,
} from "./findFamilyDoctorCityPages";
import {
  getCities,
  getLanguages,
  getVisitTypes,
  getCoverageTypes,
  getClinicSettings,
  getAccessibilityFeatures,
  getOnSiteServices,
  searchFamilyDoctors,
} from "../../../api/familyDoctors";

import FamilyDoctorMap from "./components/FamilyDoctorMap";
import "./FindFamilyDoctorPage.css";

type DoctorCard = {
  id: string;
  doctorName: string;
  clinicName: string;
  city: string;
  acceptingNewPatients: boolean;
  goodFitFor: string[];
  gender?: string;
  clinicAddress?: string;
  languages?: string[];
  clinicId?: number;
};

type Option = { id: number; name: string };

type SortMode = "name" | "clinic";

/** Extract list from various API response formats. */
function unwrapList(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.results)) return res.results;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/** Multi-select typeahead component with pills. */
function TypeaheadMultiSelect(props: {
  label: string;
  placeholder: string;
  options: Option[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  inputId: string;
}) {
  const { label, placeholder, options, selectedIds, setSelectedIds, inputId } =
    props;

  const [text, setText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const selected = useMemo(() => {
    const set = new Set(selectedIds);
    return options.filter((o) => set.has(String(o.id)));
  }, [options, selectedIds]);

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return options.slice(0, 40);
    return options.filter((o) => o.name.toLowerCase().includes(q)).slice(0, 40);
  }, [options, text]);

  const addById = (id: string) => {
    if (!id || selectedIds.includes(id)) return;
    setSelectedIds([...selectedIds, id]);
  };

  const remove = (id: string) => {
    setSelectedIds(selectedIds.filter((x) => x !== id));
  };

  return (
    <div className="ocf-find-input-group">
      {label && (
        <label className="ocf-find-input-label" htmlFor={inputId}>
          {label}
        </label>
      )}

      <div className="ocf-find-typeahead">
        <input
          id={inputId}
          className="ocf-find-input"
          placeholder={placeholder}
          value={text}
          onFocus={() => setMenuOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setMenuOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setMenuOpen(false);
            if (e.key === "Enter") {
              e.preventDefault();
              const exact = options.find(
                (o) => o.name.toLowerCase() === text.trim().toLowerCase(),
              );
              if (exact) {
                addById(String(exact.id));
                setText("");
                setMenuOpen(false);
              } else if (filtered.length === 1) {
                addById(String(filtered[0].id));
                setText("");
                setMenuOpen(false);
              }
            }
          }}
          onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
        />

        {menuOpen && (
          <div className="ocf-find-typeahead-menu">
            {filtered.length === 0 ? (
              <div className="ocf-find-typeahead-empty">No matches</div>
            ) : (
              filtered.map((o) => {
                const id = String(o.id);
                const isSelected = selectedIds.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`ocf-find-typeahead-item ${isSelected ? "is-selected" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (!isSelected) addById(id);
                      setText("");
                      setMenuOpen(false);
                    }}
                  >
                    {o.name}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="ocf-find-pills">
          {selected.map((o) => (
            <button
              key={o.id}
              type="button"
              className="ocf-find-pill"
              onClick={() => remove(String(o.id))}
            >
              {o.name} <span>×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function toBool(v: any): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return v.toLowerCase() === "true" || v === "1";
  return false;
}

function pickFirst<T = any>(obj: any, keys: string[], fallback: T): T {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
  }
  return fallback;
}

function normalizeGoodFitFor(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string") {
    const parts = raw.includes("|") ? raw.split("|") : raw.split(",");
    return parts.map((x) => x.trim()).filter(Boolean);
  }
  return [];
}

function normalizeLanguages(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string")
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
}

function normalizeOptions(
  list: any[],
  idKeys: string[],
  nameKeys: string[],
): Option[] {
  return (Array.isArray(list) ? list : [])
    .map((x: any) => {
      const id = Number(pickFirst<any>(x, idKeys, 0));
      const name = String(pickFirst<any>(x, nameKeys, "")).trim();
      return { id, name };
    })
    .filter((x) => Number.isFinite(x.id) && x.id > 0 && x.name.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeSortText(value: string | undefined): string {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function getDoctorNameSortParts(name: string): {
  lastName: string;
  firstName: string;
  fullName: string;
} {
  const cleaned = String(name || "")
    .replace(/^\s*dr\.?\s+/i, "")
    .replace(/[,]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

  const parts = cleaned.split(" ").filter(Boolean);
  const lastName = parts.length > 0 ? parts[parts.length - 1] : cleaned;
  const firstName = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";

  return {
    lastName: normalizeSortText(lastName),
    firstName: normalizeSortText(firstName),
    fullName: normalizeSortText(cleaned),
  };
}

function compareDoctorCardsByName(a: DoctorCard, b: DoctorCard): number {
  const aName = getDoctorNameSortParts(a.doctorName);
  const bName = getDoctorNameSortParts(b.doctorName);

  return (
    aName.lastName.localeCompare(bName.lastName) ||
    aName.firstName.localeCompare(bName.firstName) ||
    aName.fullName.localeCompare(bName.fullName) ||
    String(a.id).localeCompare(String(b.id))
  );
}

function compareDoctorCardsByClinic(a: DoctorCard, b: DoctorCard): number {
  return (
    normalizeSortText(a.clinicName).localeCompare(
      normalizeSortText(b.clinicName),
    ) ||
    normalizeSortText(a.city).localeCompare(normalizeSortText(b.city)) ||
    compareDoctorCardsByName(a, b)
  );
}

function sortDoctorCards(cards: DoctorCard[], sortBy: SortMode): DoctorCard[] {
  const sorted = [...cards];
  sorted.sort(
    sortBy === "clinic" ? compareDoctorCardsByClinic : compareDoctorCardsByName,
  );
  return sorted;
}

/** Postal helpers */
function normalizePostal6(raw: string): string {
  return String(raw || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .slice(0, 6);
}
function isFullPostal6(raw: string): boolean {
  const pc = normalizePostal6(raw);
  return /^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/.test(pc);
}

/** Icons */
const SearchIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const FilterIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
  </svg>
);

const LocationIcon = () => (
  <svg
    className="ocf-find-card-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const UserIcon = () => (
  <svg
    className="ocf-find-card-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const GlobeIcon = () => (
  <svg
    className="ocf-find-card-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

/** Skeleton loader */
const SkeletonCard = () => (
  <div className="ocf-find-skeleton">
    <div className="ocf-find-skeleton-avatar" />
    <div className="ocf-find-skeleton-body">
      <div className="ocf-find-skeleton-line" />
      <div className="ocf-find-skeleton-line" />
      <div className="ocf-find-skeleton-line" />
    </div>
  </div>
);

/** Doctor card */
const DoctorCardComponent: React.FC<{
  card: DoctorCard;
  isSelected: boolean;
  onRequest: () => void;
  cardRef: (el: HTMLLIElement | null) => void;
}> = ({ card, isSelected, onRequest, cardRef }) => {
  const initials = card.doctorName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <li ref={cardRef} className="ocf-find-card" data-selected={isSelected}>
      <div className="ocf-find-card-avatar">{initials || "DR"}</div>

      <div className="ocf-find-card-body">
        <div className="ocf-find-card-top">
          <h3 className="ocf-find-card-name">{card.doctorName}</h3>
          <span
            className={`ocf-find-card-status ocf-find-card-status--${
              card.acceptingNewPatients ? "open" : "closed"
            }`}
          >
            {card.acceptingNewPatients
              ? "Accepting New Patients"
              : "Not Currently Accepting New Patients"}
          </span>
        </div>

        <p className="ocf-find-card-clinic">{card.clinicName}</p>

        <div className="ocf-find-card-meta">
          {card.city && (
            <span className="ocf-find-card-meta-item">
              <LocationIcon />
              {card.city}
            </span>
          )}
          {card.gender && (
            <span className="ocf-find-card-meta-item">
              <UserIcon />
              {card.gender}
            </span>
          )}
          {card.languages && card.languages.length > 0 && (
            <span className="ocf-find-card-meta-item">
              <GlobeIcon />
              {card.languages.slice(0, 2).join(", ")}
              {card.languages.length > 2 && ` +${card.languages.length - 2}`}
            </span>
          )}
        </div>

        <div className="ocf-find-card-actions">
          <Link
            to={`/public/family-doctor/${card.id}`}
            className="ocf-find-card-btn ocf-find-card-btn--ghost"
          >
            View Profile
          </Link>

          {card.acceptingNewPatients && (
            <button
              type="button"
              className="ocf-find-card-btn ocf-find-card-btn--primary"
              onClick={onRequest}
            >
              Request to Become a Patient
            </button>
          )}
        </div>
      </div>
    </li>
  );
};

/** Empty state */
const EmptyState: React.FC<{
  onResetFilters: () => void;
  currentRadius: string;
  onExpandRadius: (newRadius: string) => void;
  hasFilters: boolean;
}> = ({ onResetFilters, currentRadius, onExpandRadius, hasFilters }) => {
  const radiusNum = Number(currentRadius);
  const nextRadius =
    radiusNum < 25
      ? "25"
      : radiusNum < 50
        ? "50"
        : radiusNum < 100
          ? "100"
          : null;

  return (
    <div className="ocf-find-empty">
      <div className="ocf-find-empty-icon">🔍</div>
      <h3>No doctors found</h3>
      <p>
        {hasFilters
          ? "No doctors match your current filters. Try adjusting your criteria."
          : `No doctors found within ${currentRadius} km of your location.`}
      </p>
      <div className="ocf-find-empty-suggestions">
        {nextRadius && (
          <button
            className="ocf-find-empty-suggestion ocf-find-empty-suggestion--primary"
            onClick={() => onExpandRadius(nextRadius)}
          >
            Expand to {nextRadius} km
          </button>
        )}
        {hasFilters && (
          <button
            className="ocf-find-empty-suggestion"
            onClick={onResetFilters}
          >
            Reset all filters
          </button>
        )}
      </div>
    </div>
  );
};

/** Secondary Community Navigator help (never a primary search action). */
const CommunityNavigatorHelpPanel: React.FC<{
  variant: "results" | "filter-zero" | "geographic-zero";
  onCheckHelp: () => void;
}> = ({ variant, onCheckHelp }) => {
  const title =
    variant === "geographic-zero"
      ? "Need help finding another option?"
      : "Need help with your search?";

  return (
    <aside
      className={`ocf-find-navigator-help${
        variant === "geographic-zero" ? " ocf-find-navigator-help--emphasized" : ""
      }`}
      aria-label="Community Navigator help"
    >
      <div className="ocf-find-navigator-help-copy">
        <h3 className="ocf-find-navigator-help-title">{title}</h3>
        <p className="ocf-find-navigator-help-text">
          Community Navigator support may be available in your area.
        </p>
      </div>
      <button
        type="button"
        className="ocf-find-navigator-help-btn"
        onClick={onCheckHelp}
      >
        Check for Community Navigator help
      </button>
    </aside>
  );
};

/** Filters Drawer */
const FiltersDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onReset: () => void;
  onApply: () => void;
  filterCount: number;
  children: React.ReactNode;
}> = ({ isOpen, onClose, onReset, onApply, filterCount, children }) => {
  if (!isOpen) return null;

  return (
    <div className="ocf-find-drawer-overlay" onClick={onClose}>
      <div className="ocf-find-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="ocf-find-drawer-header">
          <span className="ocf-find-drawer-title">
            Filters {filterCount > 0 && `(${filterCount})`}
          </span>
          <button className="ocf-find-drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="ocf-find-drawer-body">{children}</div>

        <div className="ocf-find-drawer-footer">
          <button
            className="ocf-find-btn ocf-find-btn--ghost"
            onClick={onReset}
          >
            Reset
          </button>
          <button
            className="ocf-find-btn ocf-find-btn--primary"
            onClick={onApply}
          >
            Show Results
          </button>
        </div>
      </div>
    </div>
  );
};

const GENERIC_SEO_TITLE = "Find a Family Doctor";
const GENERIC_SEO_DESCRIPTION =
  "Search for participating family doctors accepting new patients in Ontario. Filter by location, language, gender, and clinic features to narrow your search.";
const GENERIC_HEADING = "Find a Family Doctor";
const GENERIC_INTRO =
  "Search by location to find family doctors accepting new patients near you. Use filters to narrow down by language, gender, and clinic features.";

export default function FindFamilyDoctorPage() {
  const navigate = useNavigate();
  const { citySlug } = useParams<{ citySlug?: string }>();
  const cityPage = getFindFamilyDoctorCityPage(citySlug);
  const isInvalidCitySlug = Boolean(citySlug) && !cityPage;
  const routeCityName = cityPage?.cityName ?? "";

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<DoctorCard[]>([]);
  const [loadError, setLoadError] = useState("");
  const [rawRows, setRawRows] = useState<any[]>([]);

  const [cities, setCities] = useState<Option[]>([]);
  const [languages, setLanguages] = useState<Option[]>([]);
  const [visitTypes, setVisitTypes] = useState<Option[]>([]);
  const [coverageTypes, setCoverageTypes] = useState<Option[]>([]);
  const [clinicSettings, setClinicSettings] = useState<Option[]>([]);
  const [accessibilityFeatures, setAccessibilityFeatures] = useState<Option[]>(
    [],
  );
  const [onSiteServices, setOnSiteServices] = useState<Option[]>([]);

  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortMode>("name");
  const cardRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const resultsListRef = useRef<HTMLUListElement | null>(null);
  const resultsSectionRef = useRef<HTMLDivElement | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showMap, setShowMap] = useState(true);

  // Search filters (UI state)
  const [postalCode, setPostalCode] = useState("");
  const [radiusKm, setRadiusKm] = useState("10");
  const [acceptingOnly, setAcceptingOnly] = useState(true);

  // Inline postal validation (warning only; does NOT block search)
  const [postalError, setPostalError] = useState("");

  // Map center (from postal code geocoding)
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);

  // City typeahead — city routes preset this before the first search.
  const [city, setCity] = useState(routeCityName);
  const [citySearch, setCitySearch] = useState(routeCityName);
  const [cityMenuOpen, setCityMenuOpen] = useState(false);
  const [appliedCitySlug, setAppliedCitySlug] = useState(citySlug);

  if (!isInvalidCitySlug && appliedCitySlug !== citySlug) {
    setAppliedCitySlug(citySlug);
    setCity(routeCityName);
    setCitySearch(routeCityName);
  }

  // Gender (simple dropdown)
  const [gender, setGender] = useState<"Any" | "Female" | "Male">("Any");

  // Multi-select filter IDs
  const [languageIds, setLanguageIds] = useState<string[]>([]);
  const [visitTypeIds, setVisitTypeIds] = useState<string[]>([]);
  const [coverageTypeIds, setCoverageTypeIds] = useState<string[]>([]);
  const [clinicSettingIds, setClinicSettingIds] = useState<string[]>([]);
  const [accessibilityIds, setAccessibilityIds] = useState<string[]>([]);
  const [onSiteServiceIds, setOnSiteServiceIds] = useState<string[]>([]);

  // Debounced auto-search controls
  const autoSearchTimerRef = useRef<number | null>(null);
  const didInitialLoadRef = useRef(false);

  const cityFiltered = useMemo(() => {
    const q = citySearch.trim().toLowerCase();
    const list = cities || [];
    if (!q) return list.slice(0, 40);
    return list.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 40);
  }, [cities, citySearch]);

  const selectCity = (name: string) => {
    const v = String(name || "").trim();
    setCity(v);
    setCitySearch(v);
    setCityMenuOpen(false);
  };

  function buildSearchFilters() {
    // API expects a single language name (v1)
    const firstLangId = languageIds[0];
    const firstLangName = firstLangId
      ? languages.find((l) => String(l.id) === firstLangId)?.name
      : undefined;

    const vt = Number(visitTypeIds[0]);
    const ct = Number(coverageTypeIds[0]);
    const cs = Number(clinicSettingIds[0]);
    const af = Number(accessibilityIds[0]);
    const os = Number(onSiteServiceIds[0]);

    return {
      acceptingNewPatientsOnly: acceptingOnly,
      city: city || undefined,
      postalCode: normalizePostal6(postalCode) || undefined,
      radiusKm:
        Number.isFinite(Number(radiusKm)) && Number(radiusKm) > 0
          ? Number(radiusKm)
          : undefined,
      doctorGender: gender !== "Any" ? gender : undefined,
      language: firstLangName || undefined,
      visitTypeId: Number.isFinite(vt) && vt > 0 ? vt : undefined,
      coverageTypeId: Number.isFinite(ct) && ct > 0 ? ct : undefined,
      clinicSettingId: Number.isFinite(cs) && cs > 0 ? cs : undefined,
      accessibilityFeatureId: Number.isFinite(af) && af > 0 ? af : undefined,
      onSiteServiceId: Number.isFinite(os) && os > 0 ? os : undefined,
    };
  }

  function buildEffectiveFilters(): any {
    const base = buildSearchFilters();
    const pc6 = normalizePostal6(postalCode);

    // Postal empty => treat as "no geo filter"
    if (!pc6) {
      if (postalError) setPostalError("");
      const { postalCode: _pc, radiusKm: _rk, ...rest } = base;
      return rest;
    }

    // Postal present but invalid => warn, but still search using other filters (ignore postal+radius)
    if (!isFullPostal6(pc6)) {
      const msg = "Use full postal code (e.g., X0X 0X0).";
      if (postalError !== msg) setPostalError(msg);
      const { postalCode: _pc, radiusKm: _rk, ...rest } = base;
      return rest;
    }

    // Postal valid => include postal+radius (clear any warning)
    if (postalError) setPostalError("");
    return base;
  }

  async function geocodePostalCode(
    pc: string,
  ): Promise<[number, number] | null> {
    const pc6 = normalizePostal6(pc);
    if (!pc6 || !isFullPostal6(pc6)) return null;

    const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    if (!token) return null;

    try {
      const pretty = `${pc6.slice(0, 3)} ${pc6.slice(3)}`;
      const query = encodeURIComponent(`${pretty}, Ontario, Canada`);
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json` +
        `?access_token=${token}&country=CA&types=postcode&limit=1&autocomplete=false`;

      const res = await fetch(url);
      if (!res.ok) return null;

      const data = await res.json();
      const coords = data?.features?.[0]?.center;
      if (Array.isArray(coords) && coords.length === 2) {
        return [coords[0], coords[1]] as [number, number]; // [lng, lat]
      }
    } catch {
      // ignore
    }
    return null;
  }

  async function loadResults(opts?: {
    ignoreFilters?: boolean;
    explicitFilters?: any;
  }) {
    setLoading(true);
    setLoadError("");
    setSelectedClinicId(null);

    try {
      const usingExplicit =
        typeof opts?.explicitFilters === "object" &&
        opts?.explicitFilters !== null;
      const filters = opts?.ignoreFilters
        ? {}
        : usingExplicit
          ? opts!.explicitFilters
          : buildEffectiveFilters();

      // Center map only when full postal is valid AND included in filters
      const pcForMap = opts?.ignoreFilters
        ? ""
        : filters?.postalCode
          ? String(filters.postalCode)
          : "";
      if (pcForMap && isFullPostal6(pcForMap)) {
        geocodePostalCode(pcForMap).then((coords) => {
          if (coords) setMapCenter(coords);
        });
      }

      let res: any;
      try {
        res = await (searchFamilyDoctors as any)(filters);
      } catch {
        res = await (searchFamilyDoctors as any)();
      }

      const list = unwrapList(res);
      setRawRows(list);

      const mapped: DoctorCard[] = list.map((x: any, idx: number) => {
        const idVal = pickFirst<any>(
          x,
          ["id", "FamilyDoctorID", "FamilyDoctorId", "FamilyDoctorProfileID"],
          idx + 1,
        );

        const doctorName = pickFirst<string>(
          x,
          ["doctorName", "FullName", "fullName", "Name"],
          "",
        );
        const clinicName = pickFirst<string>(
          x,
          ["ClinicName", "clinicName", "clinic"],
          "",
        );
        const cityVal = pickFirst<string>(x, ["CityName", "city", "City"], "");
        const acceptingNewPatients = toBool(
          pickFirst<any>(
            x,
            ["acceptingNewPatients", "AcceptingNewPatients"],
            false,
          ),
        );
        const genderVal = pickFirst<string>(
          x,
          ["Gender", "gender", "DoctorGender"],
          "",
        );
        const langsArr = normalizeLanguages(
          pickFirst<any>(x, ["Languages", "languages"], []),
        );
        const clinicAddress = String(
          pickFirst<string>(
            x,
            ["ClinicAddress", "ClinicFullAddress", "Address"],
            "",
          ),
        ).trim();

        const goodFitForRaw = pickFirst<any>(
          x,
          ["goodFitFor", "GoodFitFor", "Tags"],
          null,
        );
        const goodFitFor = normalizeGoodFitFor(goodFitForRaw);

        const clinicIdRaw = pickFirst<any>(
          x,
          ["ClinicID", "ClinicId", "clinicId"],
          null,
        );
        const clinicIdNum = clinicIdRaw != null ? Number(clinicIdRaw) : NaN;

        return {
          id: String(idVal),
          doctorName: doctorName || "Family Doctor",
          clinicName: clinicName || "Clinic",
          city: cityVal || "",
          acceptingNewPatients,
          goodFitFor,
          gender: genderVal || undefined,
          languages: langsArr.length ? langsArr : undefined,
          clinicAddress: clinicAddress || undefined,
          clinicId:
            Number.isFinite(clinicIdNum) && clinicIdNum > 0
              ? clinicIdNum
              : undefined,
        };
      });

      setCards(mapped);
    } catch (e: any) {
      setLoadError(e?.message ? String(e.message) : "Failed to load results.");
      setCards([]);
      setRawRows([]);
    } finally {
      setLoading(false);
    }
  }

  // Initial load: respect default filter state (acceptingOnly defaults true => show accepting doctors on first render)
  useEffect(() => {
    if (isInvalidCitySlug) return;
    didInitialLoadRef.current = true;
    void loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Auto-search for ALL filters (debounced).
  // Rule: if postal is empty/invalid, we still search using other filters (postal+radius are ignored).
  useEffect(() => {
    if (isInvalidCitySlug) return;
    if (!didInitialLoadRef.current) return;

    if (autoSearchTimerRef.current) {
      window.clearTimeout(autoSearchTimerRef.current);
    }

    autoSearchTimerRef.current = window.setTimeout(() => {
      void loadResults();
    }, 450);

    return () => {
      if (autoSearchTimerRef.current) {
        window.clearTimeout(autoSearchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    acceptingOnly,
    city,
    postalCode,
    radiusKm,
    gender,
    languageIds,
    visitTypeIds,
    coverageTypeIds,
    clinicSettingIds,
    accessibilityIds,
    onSiteServiceIds,
    // Note: languages affects mapping firstLangName; include to keep behavior consistent after lookup load.
    languages,
  ]);

  // Lookups
  useEffect(() => {
    let alive = true;

    async function loadLookups() {
      try {
        const [cRes, lRes, vtRes, ctRes, csRes, afRes, osRes] =
          await Promise.all([
            getCities(),
            getLanguages(),
            getVisitTypes(),
            getCoverageTypes(),
            getClinicSettings(),
            getAccessibilityFeatures(),
            getOnSiteServices(),
          ]);

        const cityOpts = normalizeOptions(
          unwrapList(cRes),
          ["CityID", "CityId", "id"],
          ["Name", "name"],
        );
        const langOpts = normalizeOptions(
          unwrapList(lRes),
          ["LanguageID", "LanguageId", "id"],
          ["Name", "name"],
        );
        const vtOpts = normalizeOptions(
          unwrapList(vtRes),
          ["VisitTypeID", "VisitTypeId", "id"],
          ["Name", "name"],
        );
        const ctOpts = normalizeOptions(
          unwrapList(ctRes),
          ["CoverageTypeID", "CoverageTypeId", "id"],
          ["Name", "name"],
        );
        const csOpts = normalizeOptions(
          unwrapList(csRes),
          ["ClinicSettingID", "ClinicSettingId", "id"],
          ["Name", "name"],
        );
        const afOpts = normalizeOptions(
          unwrapList(afRes),
          ["AccessibilityFeatureID", "AccessibilityFeatureId", "id"],
          ["Name", "name"],
        );
        const osOpts = normalizeOptions(
          unwrapList(osRes),
          ["OnSiteServiceID", "OnSiteServiceId", "id"],
          ["Name", "name"],
        );

        if (!alive) return;
        setCities(cityOpts);
        setLanguages(langOpts);
        setVisitTypes(vtOpts);
        setCoverageTypes(ctOpts);
        setClinicSettings(csOpts);
        setAccessibilityFeatures(afOpts);
        setOnSiteServices(osOpts);
      } catch (err) {
        console.error("Failed to load lookup data:", err);
        if (!alive) return;
        setCities([]);
        setLanguages([]);
        setVisitTypes([]);
        setCoverageTypes([]);
        setClinicSettings([]);
        setAccessibilityFeatures([]);
        setOnSiteServices([]);
      }
    }

    loadLookups();
    return () => {
      alive = false;
    };
  }, []);

  const filterCount =
    (city ? 1 : 0) +
    (gender !== "Any" ? 1 : 0) +
    languageIds.length +
    visitTypeIds.length +
    coverageTypeIds.length +
    clinicSettingIds.length +
    accessibilityIds.length +
    onSiteServiceIds.length;

  const resetFilters = () => {
    setPostalCode("");
    setRadiusKm("10");
    setAcceptingOnly(true);
    setPostalError("");
    setCity("");
    setCitySearch("");
    setCityMenuOpen(false);
    setGender("Any");
    setLanguageIds([]);
    setVisitTypeIds([]);
    setCoverageTypeIds([]);
    setClinicSettingIds([]);
    setAccessibilityIds([]);
    setOnSiteServiceIds([]);
    setSelectedClinicId(null);

    // With auto-search enabled, the state reset will trigger a debounced reload automatically.
  };

  const hasResults = cards.length > 0;

  const goToCommunityNavigatorHelp = () => {
    const pc6 = normalizePostal6(postalCode);
    if (isFullPostal6(pc6)) {
      navigate("/public/community-navigator-help", {
        state: { postalCode: `${pc6.slice(0, 3)} ${pc6.slice(3)}` },
      });
      return;
    }
    navigate("/public/community-navigator-help");
  };

  const selectedClinicCards = useMemo(() => {
    if (selectedClinicId == null) return [];
    return cards.filter((card) => card.clinicId === selectedClinicId);
  }, [cards, selectedClinicId]);

  const selectedClinicName =
    selectedClinicCards[0]?.clinicName || "this clinic";

  const visibleCards = useMemo(() => {
    if (selectedClinicId == null) {
      return sortDoctorCards(cards, sortBy);
    }

    const focusedCards = cards.filter(
      (card) => card.clinicId === selectedClinicId,
    );

    return sortDoctorCards(focusedCards, "name");
  }, [cards, selectedClinicId, sortBy]);

  const mapItems = useMemo(() => {
    return (rawRows || [])
      .map((x: any) => {
        const clinicIdRaw = pickFirst<any>(
          x,
          ["ClinicID", "ClinicId", "clinicId"],
          null,
        );
        const clinicId = clinicIdRaw != null ? Number(clinicIdRaw) : NaN;

        const clinicName =
          pickFirst<string>(x, ["ClinicName", "clinicName"], "") || "Clinic";
        const doctorName = pickFirst<string>(
          x,
          ["FullName", "doctorName", "Name"],
          "",
        );
        const clinicAddress = String(
          pickFirst<string>(x, ["ClinicAddress", "Address"], ""),
        ).trim();
        const clinicPhone = String(
          pickFirst<string>(
            x,
            ["ClinicPhone", "Phone", "phone", "ClinicPhoneNumber"],
            "",
          ),
        ).trim();

        const latRaw = pickFirst<any>(
          x,
          ["Latitude", "ClinicLatitude", "latitude"],
          null,
        );
        const lngRaw = pickFirst<any>(
          x,
          ["Longitude", "ClinicLongitude", "longitude"],
          null,
        );
        const latNum = typeof latRaw === "number" ? latRaw : Number(latRaw);
        const lngNum = typeof lngRaw === "number" ? lngRaw : Number(lngRaw);

        return {
          ClinicID: clinicId,
          ClinicName: clinicName,
          ClinicAddress: clinicAddress || null,
          Phone: clinicPhone || null,
          Latitude: Number.isFinite(latNum) ? latNum : null,
          Longitude: Number.isFinite(lngNum) ? lngNum : null,
          DoctorName: doctorName || "",
        };
      })
      .filter(
        (c: any) =>
          Number.isFinite(c.ClinicID) &&
          c.ClinicID > 0 &&
          typeof c.Latitude === "number" &&
          typeof c.Longitude === "number",
      );
  }, [rawRows]);

  const runUserSearch = () => {
    // User-driven Search button still works (immediate search; postal invalid just ignores geo).
    void loadResults();
  };

  if (isInvalidCitySlug) {
    return <Navigate to="/public/find-family-doctor" replace />;
  }

  const pageTitle = cityPage?.seoTitle ?? GENERIC_SEO_TITLE;
  const pageDescription = cityPage?.description ?? GENERIC_SEO_DESCRIPTION;
  const pageHeading = cityPage?.heading ?? GENERIC_HEADING;
  const pageIntro = cityPage?.intro ?? GENERIC_INTRO;

  const focusResultsAfterClinicSelection = () => {
    const isMobileLayout = window.matchMedia("(max-width: 1024px)").matches;

    if (isMobileLayout) {
      setShowMap(false);
    }

    window.setTimeout(() => {
      resultsListRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      if (isMobileLayout) {
        resultsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 75);
  };

  return (
    <div className="ocf-find">
      <SEO title={pageTitle} description={pageDescription} />
      <PublicHeader variant="solid" />

      <header className="ocf-find-hero">
        <div className="ocf-find-hero-inner">
          <div className="ocf-find-hero-badge">For Ontario Residents</div>
          <h1>{pageHeading}</h1>
          <p className="ocf-find-hero-text">{pageIntro}</p>
          <p className="ocf-find-hero-text">
            Clinics review requests independently based on their current capacity and intake criteria.
          </p>
        </div>
      </header>

      <main className="ocf-find-main">
        <div className="ocf-find-searchbar">
          <div className="ocf-find-searchbar-grid">
            <div className="ocf-find-input-group">
              <label className="ocf-find-input-label" htmlFor="postal-code">
                <span>Postal Code</span>
                <span
                  style={{
                    marginLeft: 10,
                    fontWeight: 500,
                    fontSize: 12,
                    opacity: 0.75,
                  }}
                >
                  Use full postal code (e.g., X0X 0X0)
                </span>
              </label>
              <input
                id="postal-code"
                type="text"
                className="ocf-find-input"
                placeholder="e.g., M5V 2T6"
                value={postalCode}
                onChange={(e) => {
                  const cleaned = String(e.target.value || "")
                    .toUpperCase()
                    .replace(/[^A-Z0-9\s]/g, "");
                  setPostalCode(cleaned);
                  if (postalError) setPostalError("");
                }}
                onBlur={() => {
                  const pc6 = normalizePostal6(postalCode);
                  setPostalCode(
                    pc6 ? `${pc6.slice(0, 3)} ${pc6.slice(3)}`.trim() : "",
                  );
                }}
              />
              {postalError && (
                <div style={{ marginTop: 6, fontSize: 13, color: "#b42318" }}>
                  {postalError}
                </div>
              )}
            </div>

            <div className="ocf-find-input-group">
              <label className="ocf-find-input-label" htmlFor="radius">
                Radius
              </label>
              <select
                id="radius"
                className="ocf-find-select"
                value={radiusKm}
                onChange={(e) => setRadiusKm(e.target.value)}
              >
                <option value="5">5 km</option>
                <option value="10">10 km</option>
                <option value="25">25 km</option>
                <option value="50">50 km</option>
                <option value="100">100 km</option>
              </select>
            </div>

            <button
              type="button"
              className="ocf-find-btn ocf-find-btn--ghost"
              onClick={() => setFiltersOpen(true)}
            >
              <FilterIcon />
              Filters {filterCount > 0 && `(${filterCount})`}
            </button>

            <button
              type="button"
              className="ocf-find-btn ocf-find-btn--primary"
              onClick={runUserSearch}
              disabled={loading}
            >
              <SearchIcon />
              {loading ? "Searching..." : "Search"}
            </button>
          </div>

          <div className="ocf-find-searchbar-toggle">
            <label className="ocf-find-checkbox">
              <input
                type="checkbox"
                checked={acceptingOnly}
                onChange={(e) => setAcceptingOnly(e.target.checked)}
              />
              <span>Only show doctors accepting new patients</span>
            </label>
          </div>
        </div>

        <div className="ocf-find-map-toggle">
          <button
            type="button"
            className={`ocf-find-map-toggle-btn ${!showMap ? "is-active" : ""}`}
            onClick={() => setShowMap(false)}
          >
            List
          </button>
          <button
            type="button"
            className={`ocf-find-map-toggle-btn ${showMap ? "is-active" : ""}`}
            onClick={() => setShowMap(true)}
          >
            Map
          </button>
        </div>

        <div
          className={`ocf-find-layout ${
            showMap ? "ocf-find-layout--map-first" : "ocf-find-layout--list-first"
          }`}
        >
          <div className="ocf-find-results" ref={resultsSectionRef}>
            <div className="ocf-find-results-header">
              <div>
                <h2 className="ocf-find-results-title">Results</h2>
                <span className="ocf-find-results-count">
                  {loading
                    ? "Searching..."
                    : loadError
                      ? "Error loading results"
                      : selectedClinicId != null
                        ? `${visibleCards.length} doctor${
                            visibleCards.length === 1 ? "" : "s"
                          } shown`
                        : `${cards.length} doctors found`}
                </span>
              </div>

              <div className="ocf-find-sort">
                <span className="ocf-find-sort-label">Sort by</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortMode)}
                >
                  <option value="name">Name</option>
                  <option value="clinic">Clinic</option>
                </select>
              </div>
            </div>

            {filterCount > 0 && !loading && (
              <div className="ocf-find-active-filters">
                {city && (
                  <button
                    className="ocf-find-active-filter"
                    onClick={() => {
                      setCity("");
                      setCitySearch("");
                    }}
                  >
                    {city} <span>×</span>
                  </button>
                )}

                {gender !== "Any" && (
                  <button
                    className="ocf-find-active-filter"
                    onClick={() => setGender("Any")}
                  >
                    {gender} <span>×</span>
                  </button>
                )}

                {languageIds.map((id) => {
                  const lang = languages.find((l) => String(l.id) === id);
                  return lang ? (
                    <button
                      key={id}
                      className="ocf-find-active-filter"
                      onClick={() =>
                        setLanguageIds(languageIds.filter((x) => x !== id))
                      }
                    >
                      {lang.name} <span>×</span>
                    </button>
                  ) : null;
                })}

                {visitTypeIds.map((id) => {
                  const vt = visitTypes.find((v) => String(v.id) === id);
                  return vt ? (
                    <button
                      key={id}
                      className="ocf-find-active-filter"
                      onClick={() =>
                        setVisitTypeIds(visitTypeIds.filter((x) => x !== id))
                      }
                    >
                      {vt.name} <span>×</span>
                    </button>
                  ) : null;
                })}

                {coverageTypeIds.map((id) => {
                  const ct = coverageTypes.find((c) => String(c.id) === id);
                  return ct ? (
                    <button
                      key={id}
                      className="ocf-find-active-filter"
                      onClick={() =>
                        setCoverageTypeIds(
                          coverageTypeIds.filter((x) => x !== id),
                        )
                      }
                    >
                      {ct.name} <span>×</span>
                    </button>
                  ) : null;
                })}

                {clinicSettingIds.map((id) => {
                  const cs = clinicSettings.find((c) => String(c.id) === id);
                  return cs ? (
                    <button
                      key={id}
                      className="ocf-find-active-filter"
                      onClick={() =>
                        setClinicSettingIds(
                          clinicSettingIds.filter((x) => x !== id),
                        )
                      }
                    >
                      {cs.name} <span>×</span>
                    </button>
                  ) : null;
                })}

                {accessibilityIds.map((id) => {
                  const af = accessibilityFeatures.find(
                    (a) => String(a.id) === id,
                  );
                  return af ? (
                    <button
                      key={id}
                      className="ocf-find-active-filter"
                      onClick={() =>
                        setAccessibilityIds(
                          accessibilityIds.filter((x) => x !== id),
                        )
                      }
                    >
                      {af.name} <span>×</span>
                    </button>
                  ) : null;
                })}

                {onSiteServiceIds.map((id) => {
                  const os = onSiteServices.find((o) => String(o.id) === id);
                  return os ? (
                    <button
                      key={id}
                      className="ocf-find-active-filter"
                      onClick={() =>
                        setOnSiteServiceIds(
                          onSiteServiceIds.filter((x) => x !== id),
                        )
                      }
                    >
                      {os.name} <span>×</span>
                    </button>
                  ) : null;
                })}

                <button
                  className="ocf-find-active-filter ocf-find-active-filter--clear"
                  onClick={resetFilters}
                >
                  Clear all
                </button>
              </div>
            )}

            {!loading &&
              hasResults &&
              selectedClinicId != null &&
              selectedClinicCards.length > 0 && (
                <div className="ocf-find-selected-clinic-banner">
                  <span>
                    Showing only {selectedClinicCards.length} doctor
                    {selectedClinicCards.length === 1 ? "" : "s"} from{" "}
                    <strong>{selectedClinicName}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedClinicId(null)}
                  >
                    Clear selection
                  </button>
                </div>
              )}

            {loading ? (
              <ul className="ocf-find-results-list" ref={resultsListRef}>
                {[1, 2, 3, 4].map((i) => (
                  <SkeletonCard key={i} />
                ))}
              </ul>
            ) : loadError ? (
              <div className="ocf-find-empty">
                <div className="ocf-find-empty-icon">⚠️</div>
                <h3>Something went wrong</h3>
                <p>{loadError}</p>
                <button
                  className="ocf-find-btn ocf-find-btn--primary"
                  onClick={() => loadResults()}
                >
                  Try Again
                </button>
              </div>
            ) : !hasResults ? (
              <>
                <EmptyState
                  onResetFilters={resetFilters}
                  currentRadius={radiusKm}
                  onExpandRadius={(newRadius) => setRadiusKm(newRadius)}
                  hasFilters={filterCount > 0}
                />
                <CommunityNavigatorHelpPanel
                  variant={
                    filterCount > 0 ? "filter-zero" : "geographic-zero"
                  }
                  onCheckHelp={goToCommunityNavigatorHelp}
                />
              </>
            ) : (
              <>
                <ul className="ocf-find-results-list" ref={resultsListRef}>
                  {visibleCards.map((card) => (
                    <DoctorCardComponent
                      key={card.id}
                      card={card}
                      isSelected={selectedClinicId === card.clinicId}
                      cardRef={(el) => {
                        cardRefs.current[card.id] = el;
                      }}
                      onRequest={() =>
                        navigate(`/public/family-doctor/${card.id}/request`)
                      }
                    />
                  ))}
                </ul>
                <CommunityNavigatorHelpPanel
                  variant="results"
                  onCheckHelp={goToCommunityNavigatorHelp}
                />
              </>
            )}

            <div
              style={{
                marginTop: 20,
                fontSize: 14,
                color: "var(--ocf-text-muted)",
              }}
            >
              Looking for something else?{" "}
              <Link
                to="/public"
                style={{
                  color: "var(--ocf-blue)",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Back to Home
              </Link>
            </div>
          </div>

          <div className="ocf-find-map-container">
            <div className="ocf-find-map">
              {mapItems.length > 0 ? (
                <FamilyDoctorMap
                  items={mapItems as any}
                  center={mapCenter}
                  onClinicSelect={(clinicId: number) => {
                    setSelectedClinicId(clinicId);
                    focusResultsAfterClinicSelection();
                  }}
                />
              ) : (
                <span>
                  {loading ? "Loading map..." : "No locations to display"}
                </span>
              )}
            </div>
          </div>
        </div>

        <nav
          className="ocf-find-city-links"
          aria-label="Find a Family Doctor by City"
        >
          <h2 className="ocf-find-city-links-title">
            Find a Family Doctor by City
          </h2>
          <ul className="ocf-find-city-links-list">
            {FIND_FAMILY_DOCTOR_CITY_PAGE_LIST.map((page) => (
              <li key={page.slug}>
                <Link to={`/public/find-family-doctor/${page.slug}`}>
                  {page.cityName}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </main>

      <FiltersDrawer
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onReset={resetFilters}
        onApply={() => {
          setFiltersOpen(false);
          // Auto-search is already active; keep button for UX, but do an immediate refresh as well.
          void loadResults();
        }}
        filterCount={filterCount}
      >
        <div className="ocf-find-filter-section">
          <div className="ocf-find-filter-title">City</div>
          <div className="ocf-find-input-group">
            <label className="ocf-find-input-label" htmlFor="fd-city-input">
              City
            </label>

            <div className="ocf-find-typeahead">
              <input
                id="fd-city-input"
                className="ocf-find-input"
                placeholder="Start typing a city..."
                value={citySearch}
                onFocus={() => setCityMenuOpen(true)}
                onChange={(e) => {
                  const v = e.target.value;
                  setCitySearch(v);
                  setCityMenuOpen(true);

                  const trimmed = v.trim();
                  if (!trimmed) {
                    setCity("");
                    return;
                  }

                  const exact = (cities || []).find(
                    (c) => c.name.toLowerCase() === trimmed.toLowerCase(),
                  );
                  setCity(exact ? exact.name : "");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setCityMenuOpen(false);
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (cityFiltered.length === 1)
                      selectCity(cityFiltered[0].name);
                  }
                }}
                onBlur={() => setTimeout(() => setCityMenuOpen(false), 150)}
              />

              {cityMenuOpen && (
                <div className="ocf-find-typeahead-menu">
                  {cityFiltered.length === 0 ? (
                    <div className="ocf-find-typeahead-empty">No matches</div>
                  ) : (
                    cityFiltered.map((c) => {
                      const isSelected =
                        Boolean(city) &&
                        c.name.toLowerCase() === city.toLowerCase();
                      return (
                        <button
                          key={c.id}
                          type="button"
                          className={`ocf-find-typeahead-item ${isSelected ? "is-selected" : ""}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectCity(c.name)}
                        >
                          {c.name}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ocf-find-filter-section">
          <div className="ocf-find-filter-title">Provider</div>

          <div className="ocf-find-input-group" style={{ marginBottom: 16 }}>
            <label className="ocf-find-input-label" htmlFor="fd-gender-input">
              Doctor gender
            </label>
            <select
              id="fd-gender-input"
              className="ocf-find-select"
              value={gender}
              onChange={(e) =>
                setGender(e.target.value as "Any" | "Female" | "Male")
              }
            >
              <option value="Any">Any</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
            </select>
          </div>

          <TypeaheadMultiSelect
            label="Languages"
            placeholder="Type and select a language..."
            options={languages}
            selectedIds={languageIds}
            setSelectedIds={setLanguageIds}
            inputId="filter-languages"
          />

          <div style={{ height: 16 }} />

          <TypeaheadMultiSelect
            label="Visit Type"
            placeholder="Type and select a visit type..."
            options={visitTypes}
            selectedIds={visitTypeIds}
            setSelectedIds={setVisitTypeIds}
            inputId="filter-visit-types"
          />

          <div style={{ height: 16 }} />

          <TypeaheadMultiSelect
            label="Coverage"
            placeholder="Type and select a coverage type..."
            options={coverageTypes}
            selectedIds={coverageTypeIds}
            setSelectedIds={setCoverageTypeIds}
            inputId="filter-coverage"
          />
        </div>

        <div className="ocf-find-filter-section">
          <div className="ocf-find-filter-title">Clinic</div>

          <TypeaheadMultiSelect
            label="Clinic Setting"
            placeholder="Type and select a clinic setting..."
            options={clinicSettings}
            selectedIds={clinicSettingIds}
            setSelectedIds={setClinicSettingIds}
            inputId="filter-clinic-setting"
          />

          <div style={{ height: 16 }} />

          <TypeaheadMultiSelect
            label="Accessibility Features"
            placeholder="Type and select an accessibility feature..."
            options={accessibilityFeatures}
            selectedIds={accessibilityIds}
            setSelectedIds={setAccessibilityIds}
            inputId="filter-accessibility"
          />

          <div style={{ height: 16 }} />

          <TypeaheadMultiSelect
            label="On-site Services"
            placeholder="Type and select an on-site service..."
            options={onSiteServices}
            selectedIds={onSiteServiceIds}
            setSelectedIds={setOnSiteServiceIds}
            inputId="filter-onsite"
          />
        </div>
      </FiltersDrawer>

      <PublicFooter />
    </div>
  );
}
